// Reine Logik der Opportunities-Seiten (Agent 5D, Runde 5): Beträge, Zeiträume, Spalten. Ohne React und ohne Importe,
// damit sie sich mit `node --test` prüfen lässt (src/opportunities/tests/opportunities.test.mjs).

export type Periode = "one_time" | "monthly" | "annual";

export const PERIODEN: { wert: Periode; label: string; kurz: string }[] = [
  { wert: "one_time", label: "Einmalig", kurz: "" },
  { wert: "monthly", label: "Monatlich", kurz: "/ Monat" },
  { wert: "annual", label: "Jährlich", kurz: "/ Jahr" },
];

export function periodeKurz(p: string | null | undefined): string {
  return PERIODEN.find((x) => x.wert === p)?.kurz ?? "";
}

// ---------------------------------------------------------------------------
// Beträge (Cent in der API, Euro in der Anzeige)
// ---------------------------------------------------------------------------

/** 123456 -> "1.234,56 €", 120000 -> "1.200 €" (Cent nur, wenn sie nicht null sind). */
export function euro(cent: number | null | undefined): string {
  const c = Math.round(cent ?? 0);
  const ganz = c % 100 === 0;
  return `${(c / 100).toLocaleString("de-DE", { minimumFractionDigits: ganz ? 0 : 2, maximumFractionDigits: 2 })} €`;
}

/** Jahreswert wie Close "annualized": monatlich mal 12, sonst unverändert. */
export function jahreswert(value: number | null | undefined, periode: string | null | undefined): number {
  const w = Math.round(value ?? 0);
  return periode === "monthly" ? w * 12 : w;
}

/** Wert einer Opportunity für die Karte: "1.200 € / Monat"; leer, wenn kein Betrag. */
export function wertText(value: number | null | undefined, periode: string | null | undefined): string {
  if (!value) return "";
  const kurz = periodeKurz(periode);
  return kurz ? `${euro(value)} ${kurz}` : euro(value);
}

/** Eingabe "1.234,56", "1234.5", "1 200" -> Cent; leer -> null; Unsinn -> NaN. */
export function centAusText(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, "");
  if (!t) return null;
  let norm = t;
  if (t.includes(",")) norm = t.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) norm = t.replace(/\./g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(norm)) return Number.NaN;
  return Math.round(Number(norm) * 100);
}

/** Cent -> Text fürs Eingabefeld: 120000 -> "1200", 123450 -> "1234,50". */
export function centZuText(cent: number | null | undefined): string {
  if (cent === null || cent === undefined) return "";
  const c = Math.round(cent);
  return c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2).replace(".", ",");
}

// ---------------------------------------------------------------------------
// Tage (YYYY-MM-DD, ohne Zeitzonenrechnung)
// ---------------------------------------------------------------------------

const zwei = (n: number) => String(n).padStart(2, "0");

function teile(d: string): [number, number, number] {
  const [j, m, t] = d.slice(0, 10).split("-").map(Number);
  return [j, m, t];
}

export function tag(j: number, m: number, t: number): string {
  const d = new Date(Date.UTC(j, m - 1, t));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

export function tagPlus(d: string, n: number): string {
  const [j, m, t] = teile(d);
  return tag(j, m, t + n);
}

/** Wochentag mit Montag = 0 … Sonntag = 6. */
function wochentagMo(d: string): number {
  const [j, m, t] = teile(d);
  return (new Date(Date.UTC(j, m - 1, t)).getUTCDay() + 6) % 7;
}

export function datumKurz(d: string | null | undefined): string {
  if (!d) return "";
  const [j, m, t] = d.slice(0, 10).split("-");
  return j && m && t ? `${t}.${m}.${j}` : d;
}

// ---------------------------------------------------------------------------
// Zeiträume (Close date / Angelegt), wie die Schnellwahlen in Close
// ---------------------------------------------------------------------------

export interface Zeitraum {
  von: string | null;
  bis: string | null;
}

export interface ZeitraumWahl {
  key: string;
  label: string;
  gruppe: "jetzt" | "vorher" | "nachher" | "offen";
}

export const ZEITRAUM_WAHLEN: ZeitraumWahl[] = [
  { key: "gestern", label: "Gestern", gruppe: "vorher" },
  { key: "letzte_woche", label: "Letzte Woche", gruppe: "vorher" },
  { key: "letzter_monat", label: "Letzter Monat", gruppe: "vorher" },
  { key: "letztes_quartal", label: "Letztes Quartal", gruppe: "vorher" },
  { key: "letztes_jahr", label: "Letztes Jahr", gruppe: "vorher" },
  { key: "heute", label: "Heute", gruppe: "jetzt" },
  { key: "diese_woche", label: "Diese Woche", gruppe: "jetzt" },
  { key: "dieser_monat", label: "Dieser Monat", gruppe: "jetzt" },
  { key: "dieses_quartal", label: "Dieses Quartal", gruppe: "jetzt" },
  { key: "dieses_jahr", label: "Dieses Jahr", gruppe: "jetzt" },
  { key: "morgen", label: "Morgen", gruppe: "nachher" },
  { key: "naechste_woche", label: "Nächste Woche", gruppe: "nachher" },
  { key: "naechster_monat", label: "Nächster Monat", gruppe: "nachher" },
  { key: "naechstes_quartal", label: "Nächstes Quartal", gruppe: "nachher" },
  { key: "naechstes_jahr", label: "Nächstes Jahr", gruppe: "nachher" },
  { key: "vor_heute", label: "Vor heute", gruppe: "offen" },
  { key: "ab_heute", label: "Ab heute", gruppe: "offen" },
];

function woche(heute: string, versatz: number): Zeitraum {
  const montag = tagPlus(heute, -wochentagMo(heute) + 7 * versatz);
  return { von: montag, bis: tagPlus(montag, 6) };
}

function monat(heute: string, versatz: number): Zeitraum {
  const [j, m] = teile(heute);
  return { von: tag(j, m + versatz, 1), bis: tag(j, m + versatz + 1, 0) };
}

function quartal(heute: string, versatz: number): Zeitraum {
  const [j, m] = teile(heute);
  const erster = Math.floor((m - 1) / 3) * 3 + 1 + versatz * 3;
  return { von: tag(j, erster, 1), bis: tag(j, erster + 3, 0) };
}

function jahr(heute: string, versatz: number): Zeitraum {
  const [j] = teile(heute);
  return { von: tag(j + versatz, 1, 1), bis: tag(j + versatz, 12, 31) };
}

/** Von und bis (beide einschließlich) für eine Schnellwahl; Wochen beginnen am Montag. */
export function zeitraumFuer(key: string, heute: string): Zeitraum | null {
  switch (key) {
    case "heute":
      return { von: heute, bis: heute };
    case "gestern":
      return { von: tagPlus(heute, -1), bis: tagPlus(heute, -1) };
    case "morgen":
      return { von: tagPlus(heute, 1), bis: tagPlus(heute, 1) };
    case "diese_woche":
      return woche(heute, 0);
    case "letzte_woche":
      return woche(heute, -1);
    case "naechste_woche":
      return woche(heute, 1);
    case "dieser_monat":
      return monat(heute, 0);
    case "letzter_monat":
      return monat(heute, -1);
    case "naechster_monat":
      return monat(heute, 1);
    case "dieses_quartal":
      return quartal(heute, 0);
    case "letztes_quartal":
      return quartal(heute, -1);
    case "naechstes_quartal":
      return quartal(heute, 1);
    case "dieses_jahr":
      return jahr(heute, 0);
    case "letztes_jahr":
      return jahr(heute, -1);
    case "naechstes_jahr":
      return jahr(heute, 1);
    case "vor_heute":
      return { von: null, bis: tagPlus(heute, -1) };
    case "ab_heute":
      return { von: heute, bis: null };
    default:
      return null;
  }
}

/** Name der Schnellwahl, die genau zu diesem Zeitraum passt, sonst null. */
export function wahlFuer(z: Zeitraum, heute: string): string | null {
  for (const w of ZEITRAUM_WAHLEN) {
    const r = zeitraumFuer(w.key, heute);
    if (r && r.von === z.von && r.bis === z.bis) return w.key;
  }
  return null;
}

/** Anzeige im Knopf: "Alle Zeit", "Diese Woche", "20.10.2026 – 27.10.2026", "ab 20.10.2026", "bis 27.10.2026". */
export function zeitraumText(z: Zeitraum, heute: string): string {
  if (!z.von && !z.bis) return "Alle Zeit";
  const wahl = wahlFuer(z, heute);
  if (wahl) return ZEITRAUM_WAHLEN.find((w) => w.key === wahl)?.label ?? "";
  if (z.von && z.bis) return z.von === z.bis ? datumKurz(z.von) : `${datumKurz(z.von)} – ${datumKurz(z.bis)}`;
  return z.von ? `ab ${datumKurz(z.von)}` : `bis ${datumKurz(z.bis)}`;
}

// ---------------------------------------------------------------------------
// Spalten der Liste
// ---------------------------------------------------------------------------

export interface OppSpalte {
  key: string;
  label: string;
  /** Sortierschlüssel der API; ohne = nicht sortierbar */
  sort?: string;
  gruppe?: string;
  /** ohne Wahl abwählbar? Der Lead steht immer da */
  fest?: boolean;
  /** rechtsbündig (Zahlen) */
  rechts?: boolean;
}

export const OPP_SPALTEN: OppSpalte[] = [
  { key: "lead", label: "Lead", sort: "lead", fest: true },
  { key: "value", label: "Wert", sort: "value", rechts: true },
  { key: "confidence", label: "Konfidenz", sort: "confidence", rechts: true },
  { key: "close_date", label: "Abschlussdatum", sort: "close_date" },
  { key: "status", label: "Status" },
  { key: "user", label: "Zuständig", sort: "user" },
  { key: "date_updated", label: "Zuletzt geändert", sort: "date_updated" },
  { key: "date_created", label: "Angelegt", sort: "date_created", gruppe: "Weitere" },
  { key: "contact", label: "Kontakt", gruppe: "Weitere" },
  { key: "lead_status", label: "Lead-Status", gruppe: "Weitere" },
  { key: "note", label: "Notiz", gruppe: "Weitere" },
  { key: "pipeline", label: "Pipeline", gruppe: "Weitere" },
];

export const STANDARD_SPALTEN = ["lead", "value", "confidence", "close_date", "status", "user", "date_updated"];

/** Gemerkte Spaltenwahl bereinigen: unbekannte Schlüssel raus, der Lead immer vorn, nichts doppelt; leer -> Standard. */
export function spaltenWahl(gemerkt: string[] | null | undefined): string[] {
  const bekannt = new Set(OPP_SPALTEN.map((s) => s.key));
  const rein = (gemerkt ?? []).filter((k, i, a) => bekannt.has(k) && a.indexOf(k) === i && k !== "lead");
  return rein.length ? ["lead", ...rein] : [...STANDARD_SPALTEN];
}

/** Sortierung als API-Wert ("-value") aus {key, ab} der Bedienleiste; key ist der Spaltenschlüssel oder der API-Schlüssel. */
export function sortAlsApi(s: { key: string; ab: boolean } | null | undefined, standard: string): string {
  if (!s) return standard;
  const spalte = OPP_SPALTEN.find((x) => x.key === s.key || x.sort === s.key);
  const feld = spalte?.sort;
  return feld ? `${s.ab ? "-" : ""}${feld}` : standard;
}

export function apiAlsSort(api: string): { key: string; ab: boolean } {
  const ab = api.startsWith("-");
  const feld = api.replace(/^-/, "");
  const spalte = OPP_SPALTEN.find((x) => x.sort === feld);
  return { key: spalte?.key ?? feld, ab };
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/** Farbe der Status-Pille nach Typ: offen gelb, gewonnen grün, verloren rot (Close-Farbnamen aus `statusFarbe`). */
export function statusFarbe(typ: string | null | undefined, farbe?: string | null): string {
  if (farbe) return farbe;
  if (typ === "won") return "green";
  if (typ === "lost") return "magenta";
  return "yellow";
}

// ---------------------------------------------------------------------------
// Board: Karte in eine andere Spalte legen (sofort in der Anzeige, vor der Antwort des Servers)
// ---------------------------------------------------------------------------

interface KarteLike {
  id: string;
  status_id: string | null;
  status_label: string | null;
  status_type: "active" | "won" | "lost" | null;
  value: number | null;
  value_period: string | null;
}

interface SpalteLike<K extends KarteLike> {
  status: { id: string; label: string; type: "active" | "won" | "lost" };
  anzahl: number;
  summe: number;
  karten: K[];
}

/** Karte aus ihrer Spalte nehmen und oben in die Zielspalte legen; Zahlen und Summen ziehen mit. Fehlt die Karte oder die
 *  Zielspalte (Spalte ausgeblendet, z. B. "nur aktive"), verschwindet die Karte nur aus der Quellspalte. Gibt die Spalten
 *  unverändert zurück, wenn die Karte nirgends steht. */
export function karteVerschieben<K extends KarteLike, S extends SpalteLike<K>>(spalten: S[], karteId: string, zielStatusId: string): S[] {
  const quelle = spalten.find((s) => s.karten.some((k) => k.id === karteId));
  if (!quelle) return spalten;
  const karte = quelle.karten.find((k) => k.id === karteId)!;
  const ziel = spalten.find((s) => s.status.id === zielStatusId);
  const wert = jahreswert(karte.value, karte.value_period);
  if (quelle === ziel) return spalten;
  const neu: K = ziel
    ? { ...karte, status_id: ziel.status.id, status_label: ziel.status.label, status_type: ziel.status.type }
    : karte;
  return spalten.map((s) => {
    if (s === quelle) return { ...s, anzahl: Math.max(0, s.anzahl - 1), summe: Math.max(0, s.summe - wert), karten: s.karten.filter((k) => k.id !== karteId) };
    if (s === ziel) return { ...s, anzahl: s.anzahl + 1, summe: s.summe + wert, karten: [neu, ...s.karten] };
    return s;
  });
}
